import { useEffect, useMemo, useState } from "react";

function formatDateTime(value) {
  if (!value) {
    return "No date recorded";
  }
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function formatLeadDate(value) {
  if (!value) {
    return "No date recorded";
  }
  return new Date(value).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatLeadTime(value) {
  if (!value) {
    return "";
  }
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit"
  });
}

function sourceLabel(source = "") {
  return "Guest lead";
}

function leadName(attendee) {
  return attendee.fullName || attendee.email || "Guest lead";
}

function leadEmail(attendee) {
  return attendee.email || "No email captured";
}

function hasLeadEmail(attendee) {
  return Boolean(attendee.email);
}

function normalizeLeadEmail(value = "") {
  return String(value || "").trim().toLowerCase().replace(/^mailto:/, "");
}

function leadDate(attendee = {}) {
  return attendee.event?.startsAt || attendee.checkedInAt || attendee.createdAt;
}

function leadSortTime(attendee) {
  const timestamp = new Date(leadDate(attendee) || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function leadLocation(attendee = {}, selectedRange) {
  return attendee.event?.location ||
    attendee.event?.chapterName ||
    attendee.chapterSnapshot?.name ||
    selectedRange?.name ||
    "Location not recorded";
}

function leadMatchesSearch(attendee, selectedRange, query) {
  if (!query) {
    return true;
  }
  const haystack = [
    leadName(attendee),
    leadEmail(attendee),
    attendee.event?.title,
    leadLocation(attendee, selectedRange),
    sourceLabel(attendee.source)
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function leadMatchesWindow(attendee, windowKey) {
  if (windowKey === "all") {
    return true;
  }

  const days = Number(windowKey);
  const value = leadDate(attendee);
  if (!value || !Number.isFinite(days)) {
    return false;
  }

  const leadTime = new Date(value).getTime();
  if (!Number.isFinite(leadTime)) {
    return false;
  }

  const now = Date.now();
  const start = now - days * 24 * 60 * 60 * 1000;
  return leadTime >= start;
}

function buildGuestLeadGroups(attendees = []) {
  const grouped = new Map();

  attendees.forEach((attendee) => {
    const normalizedEmail = normalizeLeadEmail(attendee.email);
    const leadId = normalizedEmail ? `email:${normalizedEmail}` : `attendance:${attendee.attendanceId}`;
    const existing = grouped.get(leadId) || {
      leadId,
      email: normalizedEmail,
      identitySource: normalizedEmail ? "email" : "single_visit",
      visits: []
    };
    existing.visits.push(attendee);
    grouped.set(leadId, existing);
  });

  return Array.from(grouped.values()).map((lead) => {
    const visits = lead.visits.slice().sort((a, b) => leadSortTime(b) - leadSortTime(a));
    const latestAttendance = visits[0] || {};
    const firstAttendance = visits[visits.length - 1] || latestAttendance;
    const namedVisit = visits.find((visit) => visit.fullName);
    const emailedVisit = visits.find((visit) => visit.email);
    return {
      ...lead,
      attendanceId: latestAttendance.attendanceId,
      email: emailedVisit?.email || lead.email || "",
      firstAttendance,
      fullName: namedVisit?.fullName || latestAttendance.fullName || emailedVisit?.email || "Guest lead",
      latestAttendance,
      visits
    };
  });
}

function leadGroupMatchesSearch(lead, selectedRange, query) {
  if (!query) {
    return true;
  }
  const leadHaystack = [
    leadName(lead),
    leadEmail(lead),
    `${lead.visits.length} visits`
  ].filter(Boolean).join(" ").toLowerCase();
  return leadHaystack.includes(query.toLowerCase()) ||
    lead.visits.some((visit) => leadMatchesSearch(visit, selectedRange, query));
}

function leadGroupMatchesWindow(lead, windowKey) {
  return lead.visits.some((visit) => leadMatchesWindow(visit, windowKey));
}

function latestLeadOutreach(lead) {
  return (lead?.visits || [])
    .flatMap((visit) => Array.isArray(visit.leadOutreach) ? visit.leadOutreach : [])
    .filter((outreach) => outreach?.sentAt)
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0];
}

function eventHref(attendee, selectedRange) {
  const eventId = attendee.event?.eventId;
  const rangeId = selectedRange?.rangeId;
  if (!eventId || !rangeId || typeof window === "undefined") {
    return "";
  }
  const basePath = window.location.pathname.startsWith("/workspace/")
    ? `/workspace/ranges/${encodeURIComponent(rangeId)}/apps/events`
    : `/console/ranges/${encodeURIComponent(rangeId)}/apps/events`;
  return `${basePath}?eventId=${encodeURIComponent(eventId)}#events-roster`;
}

function senderState(preferences = {}) {
  const sender = preferences?.emailSender || {};
  return {
    activeEmail: sender.activeEmail || "",
    pendingEmail: sender.pendingEmail || "",
    pendingStatus: sender.pendingStatus || "none",
    activeVerifiedAt: sender.activeVerifiedAt || "",
    lastCheckedAt: sender.lastCheckedAt || ""
  };
}

function defaultEmailSubject(attendee) {
  return attendee?.event?.title
    ? `Following up from ${attendee.event.title}`
    : "Following up from your range visit";
}

function defaultEmailBody(attendee, selectedRange) {
  const name = leadName(attendee).split(" ")[0] || "there";
  const rangeName = selectedRange?.name || "the range";
  const classLine = attendee?.event?.title ? ` after ${attendee.event.title}` : "";
  return `Hi ${name},

Thanks for visiting ${rangeName}${classLine}. I wanted to follow up and see if you have any questions or would like help with the next step.

Best,`;
}

function templateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `tmpl_${crypto.randomUUID()}`;
  }
  return `tmpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function newTemplateDraft() {
  return {
    templateId: "",
    name: "Class follow-up",
    subject: "Thanks for visiting {rangeName}",
    body: `Hi {firstName},

Thanks for visiting {rangeName} for {className}. I wanted to check whether you have questions or would like help with the next step.

Best,
{rangeName}`
  };
}

function normalizeEmailTemplates(value = []) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((template) => {
      const source = template && typeof template === "object" && !Array.isArray(template) ? template : {};
      return {
        templateId: source.templateId || templateId(),
        name: source.name || source.title || "",
        subject: source.subject || "",
        body: source.body || source.content || source.message || "",
        updatedAt: source.updatedAt || ""
      };
    })
    .filter((template) => template.name && template.subject && template.body);
}

function templateTokens(attendee, selectedRange) {
  const date = leadDate(attendee);
  const fullName = leadName(attendee);
  return {
    firstName: fullName.split(" ")[0] || "there",
    fullName,
    email: leadEmail(attendee),
    rangeName: selectedRange?.name || "the range",
    className: attendee?.event?.title || "your visit",
    date: formatLeadDate(date),
    time: formatLeadTime(date),
    location: leadLocation(attendee, selectedRange)
  };
}

function applyTemplateText(value, attendee, selectedRange) {
  const tokens = templateTokens(attendee, selectedRange);
  return String(value || "").replace(/\{([a-zA-Z]+)\}/g, (match, key) => tokens[key] || match);
}

export function NonMemberAttendeesPage({
  attendees = [],
  loading = false,
  notificationPreferences,
  onRefreshSenderEmailVerification,
  onSaveNotificationPreferences,
  onSendGuestLeadEmail,
  onStartSenderEmailVerification,
  savingNotificationPreferences = false,
  selectedRange,
  sendingGuestLeadEmailId = "",
  updatingSenderEmail = false
}) {
  const [query, setQuery] = useState("");
  const [dateWindow, setDateWindow] = useState("90");
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [senderDraft, setSenderDraft] = useState("");
  const [senderSetupOpen, setSenderSetupOpen] = useState(false);
  const [senderNotice, setSenderNotice] = useState("");
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });
  const [composerOpen, setComposerOpen] = useState(false);
  const [emailNotice, setEmailNotice] = useState("");
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState(() => {
    if (typeof window === "undefined") {
      return "leads";
    }
    const hash = window.location.hash.replace("#guest-leads-", "");
    return ["leads", "templates", "sender"].includes(hash) ? hash : "leads";
  });
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [templateDraft, setTemplateDraft] = useState(newTemplateDraft);
  const [templateNotice, setTemplateNotice] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const sender = senderState(notificationPreferences);
  const senderReady = Boolean(sender.activeEmail && sender.activeVerifiedAt);
  const senderPending = Boolean(sender.pendingEmail && sender.pendingStatus === "pending");
  const emailTemplates = useMemo(
    () => normalizeEmailTemplates(notificationPreferences?.guestLeadEmailTemplates || []),
    [notificationPreferences]
  );
  const leadsTabActive = activeWorkspaceTab === "leads";
  const templatesTabActive = activeWorkspaceTab === "templates";
  const senderTabActive = activeWorkspaceTab === "sender";
  const guestLeads = useMemo(() => buildGuestLeadGroups(attendees), [attendees]);

  useEffect(() => {
    if (!senderDraft) {
      setSenderDraft(sender.pendingEmail || sender.activeEmail || "");
    }
  }, [sender.activeEmail, sender.pendingEmail, senderDraft]);

  useEffect(() => {
    function syncWorkspaceTabFromHash() {
      const hash = window.location.hash.replace("#guest-leads-", "");
      if (["leads", "templates", "sender"].includes(hash)) {
        setActiveWorkspaceTab(hash);
      }
    }

    syncWorkspaceTabFromHash();
    window.addEventListener("hashchange", syncWorkspaceTabFromHash);
    return () => window.removeEventListener("hashchange", syncWorkspaceTabFromHash);
  }, []);

  const filteredLeads = useMemo(() => {
    const nextQuery = query.trim();
    return guestLeads
      .filter((lead) => leadGroupMatchesSearch(lead, selectedRange, nextQuery))
      .filter((lead) => leadGroupMatchesWindow(lead, dateWindow))
      .sort((a, b) => leadSortTime(b.latestAttendance) - leadSortTime(a.latestAttendance));
  }, [dateWindow, guestLeads, query, selectedRange]);

  const selectedLead = selectedLeadId
    ? filteredLeads.find((lead) => lead.leadId === selectedLeadId) ||
      guestLeads.find((lead) => lead.leadId === selectedLeadId)
    : null;
  const selectedAttendance = selectedLead?.latestAttendance || null;
  const selectedLeadLastEmail = selectedLead ? latestLeadOutreach(selectedLead) : null;
  const detailMode = Boolean(selectedLead);
  const filteredVisitCount = filteredLeads.reduce((total, lead) => total + lead.visits.length, 0);

  function selectWorkspaceTab(tab) {
    setActiveWorkspaceTab(tab);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#guest-leads-${tab}`);
    }
    setComposerOpen(false);
    if (tab !== "leads") {
      setSelectedLeadId("");
      setEmailNotice("");
    }
    if (tab !== "sender") {
      setSenderSetupOpen(false);
      setSenderNotice("");
    }
    if (tab !== "templates") {
      setTemplateEditorOpen(false);
      setTemplateNotice("");
    }
  }

  function handleLeadSelect(lead) {
    setSelectedLeadId(lead.leadId);
    setComposerOpen(false);
    setEmailNotice("");
  }

  function clearFilters() {
    setQuery("");
    setDateWindow("90");
    setSelectedLeadId("");
  }

  async function handleVerifySender(event) {
    event.preventDefault();
    setSenderNotice("");
    const email = senderDraft.trim();
    if (!email) {
      setSenderNotice("Enter the sender email.");
      return;
    }
    try {
      const preferences = await onStartSenderEmailVerification?.(email);
      const nextSender = preferences?.emailSender || {};
      if (nextSender.activeEmail === email.toLowerCase() && nextSender.activeVerifiedAt) {
        setSenderNotice("Sender email is verified for this workspace.");
      } else {
        setSenderNotice(`Verification sent to ${email}.`);
      }
      setSenderSetupOpen(false);
    } catch (err) {
      setSenderNotice(err.message || "Verification could not be started.");
    }
  }

  async function handleCheckSender() {
    const email = sender.pendingEmail || sender.activeEmail;
    if (!email) {
      return;
    }
    setSenderNotice("");
    try {
      const preferences = await onRefreshSenderEmailVerification?.(email);
      const nextSender = senderState(preferences);
      setSenderNotice(nextSender.activeEmail === email ? `${email} is verified.` : `${email} is still pending.`);
    } catch (err) {
      setSenderNotice(err.message || "Sender status could not be checked.");
    }
  }

  function openComposer() {
    if (!selectedAttendance) {
      return;
    }
    setEmailDraft({
      subject: defaultEmailSubject(selectedAttendance),
      body: defaultEmailBody(selectedAttendance, selectedRange)
    });
    setSenderSetupOpen(false);
    setActiveWorkspaceTab("leads");
    setTemplateEditorOpen(false);
    setSelectedTemplateId("");
    setEmailNotice("");
    setComposerOpen(true);
  }

  function handleApplyTemplate(nextTemplateId) {
    setSelectedTemplateId(nextTemplateId);
    const template = emailTemplates.find((item) => item.templateId === nextTemplateId);
    if (!template) {
      setEmailDraft({
        subject: defaultEmailSubject(selectedAttendance),
        body: defaultEmailBody(selectedAttendance, selectedRange)
      });
      return;
    }
    setEmailDraft({
      subject: applyTemplateText(template.subject, selectedAttendance, selectedRange),
      body: applyTemplateText(template.body, selectedAttendance, selectedRange)
    });
  }

  function startTemplateEdit(template) {
    if (!template) {
      setTemplateDraft(newTemplateDraft());
      setTemplateEditorOpen(true);
      setTemplateNotice("");
      return;
    }
    setTemplateDraft({
      templateId: template?.templateId || "",
      name: template?.name || "",
      subject: template?.subject || "",
      body: template?.body || ""
    });
    setTemplateEditorOpen(true);
    setTemplateNotice("");
  }

  async function handleSaveTemplate(event) {
    event.preventDefault();
    const name = templateDraft.name.trim();
    const subject = templateDraft.subject.trim();
    const body = templateDraft.body.trim();
    if (!name || !subject || !body) {
      setTemplateNotice("Add a template name, subject, and message.");
      return;
    }
    const nextTemplate = {
      templateId: templateDraft.templateId || templateId(),
      name,
      subject,
      body,
      updatedAt: new Date().toISOString()
    };
    const nextTemplates = emailTemplates.some((template) => template.templateId === nextTemplate.templateId)
      ? emailTemplates.map((template) => template.templateId === nextTemplate.templateId ? nextTemplate : template)
      : [nextTemplate, ...emailTemplates].slice(0, 20);
    try {
      await onSaveNotificationPreferences?.({
        ...(notificationPreferences || {}),
        guestLeadEmailTemplates: nextTemplates
      });
      setTemplateDraft(newTemplateDraft());
      setTemplateEditorOpen(false);
      setTemplateNotice("Template saved.");
    } catch (err) {
      setTemplateNotice(err.message || "Template could not be saved.");
    }
  }

  async function handleDeleteTemplate(deleteTemplateId) {
    const nextTemplates = emailTemplates.filter((template) => template.templateId !== deleteTemplateId);
    try {
      await onSaveNotificationPreferences?.({
        ...(notificationPreferences || {}),
        guestLeadEmailTemplates: nextTemplates
      });
      if (templateDraft.templateId === deleteTemplateId) {
        setTemplateDraft(newTemplateDraft());
        setTemplateEditorOpen(false);
      }
      if (selectedTemplateId === deleteTemplateId) {
        setSelectedTemplateId("");
      }
      setTemplateNotice("Template removed.");
    } catch (err) {
      setTemplateNotice(err.message || "Template could not be removed.");
    }
  }

  async function handleSendEmail(event) {
    event.preventDefault();
    if (!selectedAttendance) {
      return;
    }
    setEmailNotice("");
    try {
      await onSendGuestLeadEmail?.(selectedAttendance.attendanceId, emailDraft);
      setEmailNotice("Email sent.");
      setComposerOpen(false);
    } catch (err) {
      setEmailNotice(err.message || "Email could not be sent.");
    }
  }

  return (
    <section className="panel page-panel non-member-attendees-page">
      <div className="guest-leads-primary-tabs" aria-label="Guest Leads workspace">
        {[
          ["leads", "Leads", `${filteredLeads.length} ${filteredLeads.length === 1 ? "lead" : "leads"}`],
          ["templates", "Templates", `${emailTemplates.length} saved`],
          ["sender", "Sender", senderReady ? sender.activeEmail : senderPending ? "Pending" : "Not verified"]
        ].map(([value, label, meta]) => (
          <button
            type="button"
            className={activeWorkspaceTab === value ? "is-active" : ""}
            key={value}
            onClick={() => selectWorkspaceTab(value)}
          >
            <strong>{label}</strong>
            <span>{meta}</span>
          </button>
        ))}
      </div>

      {senderTabActive && (
        <>
          <div className={`guest-leads-sender-card ${senderReady ? "is-ready" : ""}`}>
            <div>
              <span>Sender email</span>
              <strong>{senderReady ? sender.activeEmail : senderPending ? sender.pendingEmail : "Not verified"}</strong>
              {senderPending && <small>Verify this inbox, then return here to check status.</small>}
              {senderReady && senderPending && sender.pendingEmail !== sender.activeEmail && (
                <small>Waiting on replacement: {sender.pendingEmail}</small>
              )}
              {senderReady && !senderPending && <small>Guest lead emails send from this address.</small>}
            </div>
            {(senderReady || senderPending) && (
              <div className="guest-leads-sender-actions">
                {senderPending && (
                  <button type="button" className="secondary-button" onClick={handleCheckSender} disabled={updatingSenderEmail}>
                    {updatingSenderEmail ? "Checking..." : "Check status"}
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setSenderDraft(sender.pendingEmail || sender.activeEmail || "");
                    setSenderSetupOpen((open) => !open);
                    setComposerOpen(false);
                    setActiveWorkspaceTab("sender");
                    setTemplateEditorOpen(false);
                    setSenderNotice("");
                  }}
                >
                  {senderReady ? "Replace sender" : "Change email"}
                </button>
              </div>
            )}
          </div>

          {(senderSetupOpen || (!senderReady && !senderPending)) && (
            <form className="guest-leads-sender-form" onSubmit={handleVerifySender}>
              <label>
                <span>Email to send from</span>
                <input
                  type="email"
                  value={senderDraft}
                  onChange={(event) => setSenderDraft(event.target.value)}
                  placeholder="owner@example.com"
                />
              </label>
              <button type="submit" className="primary-button" disabled={updatingSenderEmail}>
                {updatingSenderEmail ? "Sending..." : "Send verification"}
              </button>
            </form>
          )}
          {senderNotice && <div className="guest-leads-inline-notice">{senderNotice}</div>}
        </>
      )}

      {templatesTabActive && !templateEditorOpen && emailTemplates.length > 0 && (
        <div className="guest-leads-template-card guest-leads-template-command">
          <button type="button" className="secondary-button" onClick={() => startTemplateEdit(null)}>
            Create template
          </button>
        </div>
      )}

      {templatesTabActive && (
        <div className={`guest-leads-template-manager ${templateEditorOpen ? "is-editing" : ""}`}>
          <div className="guest-leads-template-list">
            {emailTemplates.length === 0 ? (
              <div className="guest-leads-template-empty">
                <strong>No templates yet</strong>
                <button type="button" className="primary-button" onClick={() => startTemplateEdit(null)}>
                  Create template
                </button>
              </div>
            ) : emailTemplates.map((template) => (
              <div className="guest-leads-template-row" key={template.templateId}>
                <div>
                  <strong>{template.name}</strong>
                  <span>{template.subject}</span>
                  <small>{String(template.body || "").replace(/\s+/g, " ").slice(0, 130)}{String(template.body || "").length > 130 ? "..." : ""}</small>
                </div>
                <div>
                  <button type="button" className="secondary-button" onClick={() => startTemplateEdit(template)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => handleDeleteTemplate(template.templateId)}
                    disabled={savingNotificationPreferences}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {templateEditorOpen && (
            <form className="guest-leads-template-form" onSubmit={handleSaveTemplate}>
              <label>
                <span>Template name</span>
                <input
                  value={templateDraft.name}
                  onChange={(event) => setTemplateDraft((current) => ({ ...current, name: event.target.value }))}
                  placeholder="After class follow-up"
                />
              </label>
              <label>
                <span>Subject</span>
                <input
                  value={templateDraft.subject}
                  onChange={(event) => setTemplateDraft((current) => ({ ...current, subject: event.target.value }))}
                  placeholder="Thanks for visiting {rangeName}"
                />
              </label>
              <label>
                <span>Message</span>
                <textarea
                  rows={7}
                  value={templateDraft.body}
                  onChange={(event) => setTemplateDraft((current) => ({ ...current, body: event.target.value }))}
                  placeholder="Hi {firstName},"
                />
              </label>
              <div className="guest-leads-template-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setTemplateDraft(newTemplateDraft());
                    setTemplateEditorOpen(false);
                    setTemplateNotice("");
                  }}
                  disabled={savingNotificationPreferences}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={savingNotificationPreferences}>
                  {savingNotificationPreferences ? "Saving..." : "Save template"}
                </button>
              </div>
            </form>
          )}
          {templateNotice && <div className="guest-leads-inline-notice">{templateNotice}</div>}
        </div>
      )}

      {leadsTabActive && (
        <>
          {!detailMode && (
            <div className="guest-leads-heading">
              <div>
                <h3>Guest leads</h3>
                <p>{`${filteredLeads.length} ${filteredLeads.length === 1 ? "lead" : "leads"} from ${filteredVisitCount} ${filteredVisitCount === 1 ? "guest visit" : "guest visits"}`}</p>
              </div>
              <span>Newest first</span>
            </div>
          )}

          {!detailMode && (
            <div className="guest-leads-toolbar" aria-label="Guest lead filters">
                <label>
                  <span>Search</span>
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setSelectedLeadId("");
                    }}
                    placeholder="Name, email, class, or location"
                  />
                </label>
                <label>
                  <span>When</span>
                  <select
                    value={dateWindow}
                    onChange={(event) => {
                      setDateWindow(event.target.value);
                      setSelectedLeadId("");
                    }}
                  >
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="365">Last year</option>
                    <option value="all">All dates</option>
                  </select>
                </label>
                {(query || dateWindow !== "90") && (
                  <button type="button" className="secondary-button" onClick={clearFilters}>
                    Clear filters
                  </button>
                )}
              </div>
          )}

          {loading && <div className="empty-state">Loading guest leads...</div>}
          {!loading && filteredLeads.length === 0 && (
            <div className="empty-state">No guest leads match this view.</div>
          )}
          {!loading && !detailMode && filteredLeads.length > 0 && (
            <div className="guest-leads-workspace">
              <div className="non-member-attendee-list" aria-label="Guest lead list">
                {filteredLeads.map((attendee) => {
                  const latestAttendance = attendee.latestAttendance;
                  const date = leadDate(latestAttendance);
                  const visitText = attendee.visits.length > 1 ? ` · ${attendee.visits.length} visits` : "";
                  return (
                    <button
                      type="button"
                      className="non-member-attendee-row"
                      key={attendee.leadId}
                      onClick={() => handleLeadSelect(attendee)}
                    >
                      <div>
                        <strong>{leadName(attendee)}</strong>
                        <span>{leadEmail(attendee)}</span>
                      </div>
                      <div>
                        <span>{formatLeadDate(date)}</span>
                        <small>{formatLeadTime(date)}{visitText}</small>
                      </div>
                      <div>
                        <span>{leadLocation(latestAttendance, selectedRange)}</span>
                        <small>{latestAttendance.event?.title || sourceLabel(latestAttendance.source)}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      {leadsTabActive && !loading && detailMode && (
        <div className="guest-lead-detail-screen">
          <aside className={`guest-lead-detail${composerOpen ? " is-composing" : ""}`} aria-label="Selected guest lead">
            <div className="guest-lead-detail-header">
              <button type="button" className="guest-lead-back-link" onClick={() => setSelectedLeadId("")}>
                Back to leads
              </button>
              <strong>{leadName(selectedLead)}</strong>
              <small>{leadEmail(selectedLead)}</small>
              {selectedLead.identitySource === "email" && selectedLead.visits.length > 1 && (
                <small className="guest-lead-identity-note">Grouped by matching email</small>
              )}
              {selectedLead.identitySource !== "email" && (
                <small className="guest-lead-identity-note">Not grouped: no email captured</small>
              )}
              <div className="guest-lead-actions">
                {hasLeadEmail(selectedLead) && !composerOpen && (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={openComposer}
                    disabled={!senderReady || sendingGuestLeadEmailId === selectedAttendance?.attendanceId}
                  >
                    {sendingGuestLeadEmailId === selectedAttendance?.attendanceId
                      ? "Sending..."
                      : senderReady ? "Email lead" : "Verify sender first"}
                  </button>
                )}
              </div>
              <div className="guest-lead-email-state">
                <span>Last email</span>
                <strong>{selectedLeadLastEmail?.sentAt ? formatDateTime(selectedLeadLastEmail.sentAt) : "No email sent"}</strong>
              </div>
            </div>

            {composerOpen ? (
              <form className="guest-lead-compose" onSubmit={handleSendEmail}>
                <div className="guest-lead-compose-heading">
                  <span>Email lead</span>
                  <strong>{leadName(selectedLead)}</strong>
                </div>
                {emailTemplates.length > 0 && (
                  <label>
                    <span>Template</span>
                    <select
                      value={selectedTemplateId}
                      onChange={(event) => handleApplyTemplate(event.target.value)}
                    >
                      <option value="">Start fresh</option>
                      {emailTemplates.map((template) => (
                        <option key={template.templateId} value={template.templateId}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  <span>Subject</span>
                  <input
                    value={emailDraft.subject}
                    onChange={(event) => setEmailDraft((current) => ({ ...current, subject: event.target.value }))}
                  />
                </label>
                <label>
                  <span>Message</span>
                  <textarea
                    rows={4}
                    value={emailDraft.body}
                    onChange={(event) => setEmailDraft((current) => ({ ...current, body: event.target.value }))}
                  />
                </label>
                <div className="guest-lead-compose-actions">
                  <button type="button" className="secondary-button" onClick={() => setComposerOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="primary-button" disabled={sendingGuestLeadEmailId === selectedAttendance?.attendanceId}>
                    {sendingGuestLeadEmailId === selectedAttendance?.attendanceId ? "Sending..." : "Send email"}
                  </button>
                </div>
              </form>
            ) : (
              <>
            {selectedLead.visits.length > 1 && (
              <div className="guest-lead-detail-grid">
                <div>
                  <span>First seen</span>
                  <strong>{formatDateTime(leadDate(selectedLead.firstAttendance))}</strong>
                </div>
                <div>
                  <span>Last seen</span>
                  <strong>{formatDateTime(leadDate(selectedAttendance))}</strong>
                </div>
                <div>
                  <span>Visits</span>
                  <strong>{selectedLead.visits.length}</strong>
                </div>
              </div>
            )}

            <div className="guest-lead-visit-history">
              <div className="guest-lead-visit-heading">
                <strong>Visit history</strong>
              </div>
              <div className="guest-lead-visit-list">
                {selectedLead.visits.map((visit) => (
                  <article className="guest-lead-visit-row" key={visit.attendanceId}>
                    <div>
                      <strong>{formatLeadDate(leadDate(visit))}</strong>
                      <span>{formatLeadTime(leadDate(visit))}</span>
                    </div>
                    <div>
                      <strong>{visit.event?.title || "Range visit"}</strong>
                      <span>{leadLocation(visit, selectedRange)}</span>
                    </div>
                    {eventHref(visit, selectedRange) && (
                      <div>
                        <a href={eventHref(visit, selectedRange)}>Open class</a>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </div>
              </>
            )}

            {emailNotice && <div className="guest-leads-inline-notice">{emailNotice}</div>}
          </aside>
        </div>
      )}
    </section>
  );
}
